import type {
  IdGenerator,
  ModerationStrikeId,
  SupportTicketAccusedPlayerId,
  SupportTicketEvidenceId,
  SupportTicketId,
  SupportTicketMessageId,
  SupportTicketLogBundleId,
  StaffTicketNotificationId,
  SupportTicketTranscriptId,
  SupportTicketWatcherId,
  UserId
} from "./ids.js";
import type { Permission } from "./access-control.js";

export type TicketViewPermission = Extract<Permission, "tickets.view" | "tickets.staff_report.view">;
export type TicketManagePermission = Extract<Permission, "tickets.manage" | "tickets.staff_report.view">;

export type SupportTicketType = "support" | "rule_question" | "player_report" | "ban_appeal" | "staff_report" | "other";
export type SupportTicketStatus =
  | "new"
  | "awaiting_triage"
  | "awaiting_reporter"
  | "under_investigation"
  | "awaiting_accused"
  | "escalated"
  | "owner_review"
  | "resolved"
  | "closed"
  | "reopened"
  // Transitional values retained while pre-case-management records are migrated.
  | "open"
  | "claimed"
  | "waiting_player";
export type SupportTicketPriority = "low" | "normal" | "high" | "urgent";
export type SupportTicketVisibility = "staff" | "owner";
export type SupportTicketResolutionType =
  | "no_action"
  | "warning"
  | "mute"
  | "suspension"
  | "ban"
  | "insufficient_evidence"
  | "duplicate"
  | "invalid_report"
  | "redirected_support"
  | "reporter_withdrew"
  | "custom";

export interface SupportTicket {
  id: SupportTicketId;
  reference: string;
  type: SupportTicketType;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  tags: string[];
  visibility: SupportTicketVisibility;
  creatorUserId: UserId;
  subject: string;
  body: string;
  targetUserId?: UserId;
  targetSteam64Id?: string;
  appealedStrikeId?: ModerationStrikeId;
  evidenceUrl?: string;
  submissionKey?: string;
  claimedByUserId?: UserId;
  resolutionType?: SupportTicketResolutionType;
  closureNote?: string;
  reporterClosureMessage?: string;
  resolvedByUserId?: UserId;
  resolvedAt?: Date;
  dueAt?: Date;
  escalationReason?: string;
  discordChannelId?: string;
  discordChannelBoundAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  closedAt?: Date;
}

export interface SupportTicketMessage {
  id: SupportTicketMessageId;
  ticketId: SupportTicketId;
  authorUserId: UserId;
  body: string;
  internal: boolean;
  createdAt: Date;
}

export interface SupportTicketTranscript {
  id: SupportTicketTranscriptId;
  ticketId: SupportTicketId;
  createdByUserId: UserId;
  format: "markdown";
  content: string;
  messageCount: number;
  internalMessageCount: number;
  createdAt: Date;
}

export interface SupportTicketAccusedPlayer {
  id: SupportTicketAccusedPlayerId;
  ticketId: SupportTicketId;
  userId?: UserId;
  steam64Id?: string;
  displayName?: string;
  addedByUserId: UserId;
  addedAt: Date;
  removedAt?: Date;
}

export type SupportTicketEvidenceSource = "reporter" | "staff" | "system";
export type SupportTicketEvidenceReviewStatus = "pending" | "reviewed" | "relevant" | "inconclusive" | "rejected";

interface SupportTicketEvidenceBase {
  id: SupportTicketEvidenceId;
  ticketId: SupportTicketId;
  source: SupportTicketEvidenceSource;
  label: string;
  submittedByUserId: UserId;
  reviewStatus: SupportTicketEvidenceReviewStatus;
  reviewedByUserId?: UserId;
  reviewReason?: string;
  createdAt: Date;
  reviewedAt?: Date;
}

export interface SupportTicketLinkEvidence extends SupportTicketEvidenceBase {
  kind: "link";
  url: string;
}

export interface SupportTicketUploadEvidence extends SupportTicketEvidenceBase {
  kind: "upload";
  storageKey: string;
  originalFileName: string;
  contentType: string;
  byteSize: number;
  sha256: string;
}

export interface SupportTicketLogEvidence extends SupportTicketEvidenceBase {
  kind: "log";
  logBundleId: SupportTicketLogBundleId;
  eventCount: number;
  sha256: string;
}

export type SupportTicketEvidence = SupportTicketLinkEvidence | SupportTicketUploadEvidence | SupportTicketLogEvidence;

export interface SupportTicketWatcher {
  id: SupportTicketWatcherId;
  ticketId: SupportTicketId;
  userId: UserId;
  addedByUserId: UserId;
  createdAt: Date;
}

export interface StaffTicketNotificationPreference {
  userId: UserId;
  inAppEnabled: boolean;
  discordDmEnabled: boolean;
  notifyAssignment: boolean;
  notifyWatchedCase: boolean;
  notifyReporterReply: boolean;
  updatedAt: Date;
}

export type StaffTicketNotificationType =
  | "assignment"
  | "watched_case"
  | "reporter_reply"
  | "escalation"
  | "ticket_update"
  | "ticket_closed"
  | "moderation";

export interface StaffTicketNotification {
  id: StaffTicketNotificationId;
  userId: UserId;
  ticketId: SupportTicketId;
  type: StaffTicketNotificationType;
  title: string;
  body: string;
  createdAt: Date;
  readAt?: Date;
}

export function defaultStaffTicketNotificationPreference(userId: UserId, now: Date): StaffTicketNotificationPreference {
  return {
    userId,
    inAppEnabled: true,
    discordDmEnabled: false,
    notifyAssignment: true,
    notifyWatchedCase: true,
    notifyReporterReply: true,
    updatedAt: now
  };
}

export function createStaffTicketNotification(
  ids: IdGenerator,
  input: Omit<StaffTicketNotification, "id" | "createdAt" | "readAt"> & { now: Date }
): StaffTicketNotification {
  const title = input.title.trim();
  const body = input.body.trim();
  if (!title || title.length > 160) throw new Error("Ticket notifications require a short title.");
  if (!body || body.length > 1_000) throw new Error("Ticket notifications require a concise body.");
  return {
    id: ids.next("ticketnotice") as StaffTicketNotificationId,
    userId: input.userId,
    ticketId: input.ticketId,
    type: input.type,
    title,
    body,
    createdAt: input.now
  };
}

export interface SupportTicketLogBundle {
  id: SupportTicketLogBundleId;
  ticketId: SupportTicketId;
  purpose: "capture" | "evidence";
  sourceBundleId?: SupportTicketLogBundleId;
  participantRole: "reporter" | "accused";
  participantUserId?: UserId;
  participantSteam64Id?: string;
  participantDisplayName?: string;
  windowStart: Date;
  windowEnd: Date;
  eventCount: number;
  truncated: boolean;
  content: string;
  sha256: string;
  createdByUserId: UserId;
  createdAt: Date;
}

export interface CreateSupportTicketLogBundleInput {
  ticketId: SupportTicketId;
  purpose?: SupportTicketLogBundle["purpose"];
  sourceBundleId?: SupportTicketLogBundleId;
  participantRole: SupportTicketLogBundle["participantRole"];
  participantUserId?: UserId;
  participantSteam64Id?: string;
  participantDisplayName?: string;
  windowStart: Date;
  windowEnd: Date;
  eventCount: number;
  truncated?: boolean;
  content: string;
  sha256: string;
  createdByUserId: UserId;
  now: Date;
}

export function createSupportTicketLogBundle(ids: IdGenerator, input: CreateSupportTicketLogBundleInput): SupportTicketLogBundle {
  if (!input.participantUserId && !input.participantSteam64Id) {
    throw new Error("Log bundles require a linked user or Steam64 ID.");
  }
  if (input.windowStart > input.windowEnd) {
    throw new Error("Log bundle start must be before its end.");
  }
  if (!Number.isInteger(input.eventCount) || input.eventCount < 0 || input.eventCount > 5_000) {
    throw new Error("Log bundles support up to 5,000 events.");
  }
  if (!input.content.trim()) {
    throw new Error("Log bundles require immutable content.");
  }
  if (!/^[a-f0-9]{64}$/.test(input.sha256)) {
    throw new Error("Log bundles require a SHA-256 digest.");
  }
  return {
    id: ids.next("ticketlog") as SupportTicketLogBundleId,
    ticketId: input.ticketId,
    purpose: input.purpose ?? "capture",
    ...(input.sourceBundleId ? { sourceBundleId: input.sourceBundleId } : {}),
    participantRole: input.participantRole,
    ...(input.participantUserId ? { participantUserId: input.participantUserId } : {}),
    ...(input.participantSteam64Id ? { participantSteam64Id: input.participantSteam64Id } : {}),
    ...(input.participantDisplayName?.trim() ? { participantDisplayName: input.participantDisplayName.trim() } : {}),
    windowStart: input.windowStart,
    windowEnd: input.windowEnd,
    eventCount: input.eventCount,
    truncated: input.truncated ?? false,
    content: input.content,
    sha256: input.sha256,
    createdByUserId: input.createdByUserId,
    createdAt: input.now
  };
}

export type ReporterVisibleSupportTicketEvidence =
  | Omit<SupportTicketLinkEvidence, "reviewStatus" | "reviewedByUserId" | "reviewReason" | "reviewedAt">
  | Omit<SupportTicketUploadEvidence, "storageKey" | "reviewStatus" | "reviewedByUserId" | "reviewReason" | "reviewedAt">
  | Omit<SupportTicketLogEvidence, "reviewStatus" | "reviewedByUserId" | "reviewReason" | "reviewedAt">;

export interface CreateSupportTicketEvidenceInput {
  ticketId: SupportTicketId;
  source: SupportTicketEvidenceSource;
  label: string;
  url: string;
  submittedByUserId: UserId;
  now: Date;
}

export interface CreateSupportTicketUploadEvidenceInput {
  ticketId: SupportTicketId;
  source: SupportTicketEvidenceSource;
  label: string;
  storageKey: string;
  originalFileName: string;
  contentType: string;
  byteSize: number;
  sha256: string;
  submittedByUserId: UserId;
  now: Date;
}

export interface CreateSupportTicketLogEvidenceInput {
  ticketId: SupportTicketId;
  source: "staff" | "system";
  label: string;
  logBundleId: SupportTicketLogBundleId;
  eventCount: number;
  sha256: string;
  submittedByUserId: UserId;
  now: Date;
}

export interface CreateSupportTicketAccusedPlayerInput {
  ticketId: SupportTicketId;
  userId?: UserId;
  steam64Id?: string;
  displayName?: string;
  addedByUserId: UserId;
  now: Date;
}

export type ReporterVisibleSupportTicket = Omit<
  SupportTicket,
  | "targetUserId"
  | "targetSteam64Id"
  | "claimedByUserId"
  | "tags"
  | "submissionKey"
  | "closureNote"
  | "resolvedByUserId"
  | "dueAt"
  | "escalationReason"
  | "discordChannelId"
  | "discordChannelBoundAt"
>;

export interface CreateSupportTicketInput {
  type: SupportTicketType;
  creatorUserId: UserId;
  subject: string;
  body: string;
  targetUserId?: UserId;
  targetSteam64Id?: string;
  appealedStrikeId?: ModerationStrikeId;
  evidenceUrl?: string;
  submissionKey?: string;
  priority?: SupportTicketPriority;
  now: Date;
}

export interface CreateSupportTicketMessageInput {
  ticketId: SupportTicketId;
  authorUserId: UserId;
  body: string;
  internal?: boolean;
  now: Date;
}

export interface UpdateSupportTicketStatusInput {
  status: Exclude<SupportTicketStatus, "closed">;
  actorUserId: UserId;
  escalationReason?: string;
  now: Date;
}

export interface UpdateSupportTicketOperationsInput {
  actorUserId: UserId;
  claimedByUserId?: UserId | null;
  priority?: SupportTicketPriority;
  dueAt?: Date | null;
  now: Date;
}

export interface CloseSupportTicketInput {
  actorUserId: UserId;
  resolutionType: SupportTicketResolutionType;
  closureNote: string;
  reporterClosureMessage?: string;
  now: Date;
}

export interface CreateSupportTicketTranscriptInput {
  ticket: SupportTicket;
  messages: SupportTicketMessage[];
  createdByUserId: UserId;
  now: Date;
  discordTranscript?: string;
  discordMessageCount?: number;
}

export function createSupportTicket(ids: IdGenerator, input: CreateSupportTicketInput): SupportTicket {
  const subject = input.subject.trim();
  const body = input.body.trim();

  if (!subject) {
    throw new Error("Tickets require a subject.");
  }

  if (!body) {
    throw new Error("Tickets require a message.");
  }

  if (input.targetUserId === input.creatorUserId) {
    throw new Error("The ticket creator cannot also be the reported player.");
  }

  const id = ids.next("ticket") as SupportTicketId;
  return {
    id,
    reference: supportTicketReference(id),
    type: input.type,
    status: "new",
    priority: input.priority ?? "normal",
    tags: [],
    visibility: input.type === "staff_report" ? "owner" : "staff",
    creatorUserId: input.creatorUserId,
    subject,
    body,
    ...(input.targetUserId ? { targetUserId: input.targetUserId } : {}),
    ...(input.targetSteam64Id ? { targetSteam64Id: input.targetSteam64Id } : {}),
    ...(input.appealedStrikeId ? { appealedStrikeId: input.appealedStrikeId } : {}),
    ...(input.evidenceUrl ? { evidenceUrl: input.evidenceUrl } : {}),
    ...(input.submissionKey ? { submissionKey: input.submissionKey } : {}),
    createdAt: input.now,
    updatedAt: input.now
  };
}

export function createSupportTicketEvidence(
  ids: IdGenerator,
  input: CreateSupportTicketEvidenceInput
): SupportTicketEvidence {
  const label = input.label.trim();
  if (label.length < 2 || label.length > 120) {
    throw new Error("Evidence requires a label between 2 and 120 characters.");
  }
  let parsed: URL;
  try {
    parsed = new URL(input.url);
  } catch {
    throw new Error("Evidence requires a valid URL.");
  }
  if (parsed.protocol !== "https:") {
    throw new Error("Evidence links must use HTTPS.");
  }
  if (parsed.username || parsed.password) {
    throw new Error("Evidence links cannot contain embedded credentials.");
  }

  return {
    id: ids.next("ticketevidence") as SupportTicketEvidenceId,
    ticketId: input.ticketId,
    source: input.source,
    kind: "link",
    label,
    url: parsed.toString(),
    submittedByUserId: input.submittedByUserId,
    reviewStatus: "pending",
    createdAt: input.now
  };
}

export function createSupportTicketUploadEvidence(
  ids: IdGenerator,
  input: CreateSupportTicketUploadEvidenceInput
): SupportTicketUploadEvidence {
  const label = input.label.trim();
  if (label.length < 2 || label.length > 120) throw new Error("Evidence requires a label between 2 and 120 characters.");
  if (!/^[A-Za-z0-9/_-]{20,300}$/.test(input.storageKey) || input.storageKey.includes("..")) {
    throw new Error("Evidence storage key is invalid.");
  }
  const originalFileName = input.originalFileName.trim();
  if (!originalFileName || originalFileName.length > 160 || /[\\/\0]/.test(originalFileName)) {
    throw new Error("Evidence filename is invalid.");
  }
  if (!/^[a-z0-9.+-]+\/[a-z0-9.+-]+$/i.test(input.contentType)) throw new Error("Evidence content type is invalid.");
  if (!Number.isSafeInteger(input.byteSize) || input.byteSize < 1) throw new Error("Evidence file size is invalid.");
  if (!/^[a-f0-9]{64}$/.test(input.sha256)) throw new Error("Evidence checksum is invalid.");

  return {
    id: ids.next("ticketevidence") as SupportTicketEvidenceId,
    ticketId: input.ticketId,
    source: input.source,
    kind: "upload",
    label,
    storageKey: input.storageKey,
    originalFileName,
    contentType: input.contentType,
    byteSize: input.byteSize,
    sha256: input.sha256,
    submittedByUserId: input.submittedByUserId,
    reviewStatus: "pending",
    createdAt: input.now
  };
}

export function createSupportTicketLogEvidence(
  ids: IdGenerator,
  input: CreateSupportTicketLogEvidenceInput
): SupportTicketLogEvidence {
  const label = input.label.trim();
  if (label.length < 2 || label.length > 120) throw new Error("Evidence requires a label between 2 and 120 characters.");
  if (!Number.isInteger(input.eventCount) || input.eventCount < 1 || input.eventCount > 5_000) {
    throw new Error("Log evidence must contain between 1 and 5,000 events.");
  }
  if (!/^[a-f0-9]{64}$/.test(input.sha256)) throw new Error("Log evidence checksum is invalid.");

  return {
    id: ids.next("ticketevidence") as SupportTicketEvidenceId,
    ticketId: input.ticketId,
    source: input.source,
    kind: "log",
    label,
    logBundleId: input.logBundleId,
    eventCount: input.eventCount,
    sha256: input.sha256,
    submittedByUserId: input.submittedByUserId,
    reviewStatus: "pending",
    createdAt: input.now
  };
}

export function createSupportTicketWatcher(
  ids: IdGenerator,
  input: { ticketId: SupportTicketId; userId: UserId; addedByUserId: UserId; now: Date }
): SupportTicketWatcher {
  return {
    id: ids.next("ticketwatcher") as SupportTicketWatcherId,
    ticketId: input.ticketId,
    userId: input.userId,
    addedByUserId: input.addedByUserId,
    createdAt: input.now
  };
}

export function reviewSupportTicketEvidence(
  evidence: SupportTicketEvidence,
  input: {
    status: Exclude<SupportTicketEvidenceReviewStatus, "pending">;
    reviewedByUserId: UserId;
    reason?: string;
    now: Date;
  }
): SupportTicketEvidence {
  const reason = input.reason?.trim();
  if ((input.status === "rejected" || input.status === "inconclusive") && (!reason || reason.length < 3)) {
    throw new Error("Rejected or inconclusive evidence requires a review reason.");
  }
  if (reason && reason.length > 1_000) {
    throw new Error("Evidence review reasons cannot exceed 1000 characters.");
  }
  return {
    ...evidence,
    reviewStatus: input.status,
    reviewedByUserId: input.reviewedByUserId,
    ...(reason ? { reviewReason: reason } : { reviewReason: undefined }),
    reviewedAt: input.now
  };
}

export function evidenceForReporter(evidence: SupportTicketEvidence): ReporterVisibleSupportTicketEvidence {
  const {
    reviewStatus: _reviewStatus,
    reviewedByUserId: _reviewedByUserId,
    reviewReason: _reviewReason,
    reviewedAt: _reviewedAt,
    ...visibleWithStorage
  } = evidence;
  if (evidence.kind === "upload") {
    const { storageKey: _storageKey, ...visible } = visibleWithStorage as Omit<SupportTicketUploadEvidence, "reviewStatus" | "reviewedByUserId" | "reviewReason" | "reviewedAt">;
    return visible;
  }
  if (evidence.kind === "log") {
    return visibleWithStorage as Omit<SupportTicketLogEvidence, "reviewStatus" | "reviewedByUserId" | "reviewReason" | "reviewedAt">;
  }
  return visibleWithStorage as ReporterVisibleSupportTicketEvidence;
}

export function ticketForReporter(ticket: SupportTicket): ReporterVisibleSupportTicket {
  const {
    targetUserId: _targetUserId,
    targetSteam64Id: _targetSteam64Id,
    submissionKey: _submissionKey,
    claimedByUserId: _claimedByUserId,
    tags: _tags,
    closureNote: _closureNote,
    resolvedByUserId: _resolvedByUserId,
    dueAt: _dueAt,
    escalationReason: _escalationReason,
    ...visibleTicket
  } = ticket;
  return {
    ...visibleTicket,
    // Legacy records may contain this internal workflow state. Reporters must not
    // infer that an accused-player contact step exists.
    status: visibleTicket.status === "awaiting_accused" ? "under_investigation" : visibleTicket.status
  };
}

export function ticketMessagesForReporter(messages: SupportTicketMessage[]): SupportTicketMessage[] {
  return messages.filter((message) => !message.internal);
}

export function createSupportTicketAccusedPlayer(
  ids: IdGenerator,
  input: CreateSupportTicketAccusedPlayerInput
): SupportTicketAccusedPlayer {
  const steam64Id = input.steam64Id?.trim();
  const displayName = input.displayName?.trim();
  if (!input.userId && !steam64Id) {
    throw new Error("Accused-player attachments require a linked user or Steam64 ID.");
  }
  if (steam64Id && !/^7656\d{13}$/.test(steam64Id)) {
    throw new Error("Accused-player Steam64 ID is invalid.");
  }
  return {
    id: ids.next("ticketaccused") as SupportTicketAccusedPlayerId,
    ticketId: input.ticketId,
    ...(input.userId ? { userId: input.userId } : {}),
    ...(steam64Id ? { steam64Id } : {}),
    ...(displayName ? { displayName } : {}),
    addedByUserId: input.addedByUserId,
    addedAt: input.now
  };
}

export function createSupportTicketMessage(ids: IdGenerator, input: CreateSupportTicketMessageInput): SupportTicketMessage {
  const body = input.body.trim();

  if (!body) {
    throw new Error("Ticket messages cannot be empty.");
  }

  return {
    id: ids.next("ticketmsg") as SupportTicketMessageId,
    ticketId: input.ticketId,
    authorUserId: input.authorUserId,
    body,
    internal: input.internal ?? false,
    createdAt: input.now
  };
}

export function claimSupportTicket(ticket: SupportTicket, actorUserId: UserId, now: Date): SupportTicket {
  assertTicketOpenForStaffAction(ticket);

  return {
    ...ticket,
    status: "under_investigation",
    claimedByUserId: actorUserId,
    updatedAt: now
  };
}

export function updateSupportTicketOperations(
  ticket: SupportTicket,
  input: UpdateSupportTicketOperationsInput
): SupportTicket {
  assertTicketOpenForStaffAction(ticket);
  if (input.dueAt && input.dueAt.getTime() <= input.now.getTime()) {
    throw new Error("Ticket due dates must be in the future.");
  }

  const claimedByUserId = input.claimedByUserId === undefined
    ? ticket.claimedByUserId
    : input.claimedByUserId ?? undefined;
  const status = claimedByUserId && (ticket.status === "new" || ticket.status === "awaiting_triage" || ticket.status === "open")
    ? "under_investigation"
    : ticket.status;

  return {
    ...ticket,
    status,
    claimedByUserId,
    priority: input.priority ?? ticket.priority,
    dueAt: input.dueAt === undefined ? ticket.dueAt : input.dueAt ?? undefined,
    updatedAt: input.now
  };
}

export function updateSupportTicketStatus(ticket: SupportTicket, input: UpdateSupportTicketStatusInput): SupportTicket {
  if (ticket.status === "closed" && input.status !== "reopened") {
    throw new Error("Closed tickets must be reopened before they can be changed.");
  }

  if ((input.status === "claimed" || input.status === "under_investigation") && !ticket.claimedByUserId) {
    return claimSupportTicket(ticket, input.actorUserId, input.now);
  }

  if (input.status === "awaiting_accused") {
    throw new Error("Accused players are never contacted or notified through the case system.");
  }

  const escalationReason = input.escalationReason?.trim();
  if ((input.status === "escalated" || input.status === "owner_review") && (!escalationReason || escalationReason.length < 5)) {
    throw new Error("Escalating a case requires a meaningful reason.");
  }

  return {
    ...ticket,
    status: input.status,
    ...(input.status === "escalated" || input.status === "owner_review" ? { escalationReason } : {}),
    ...(input.status === "reopened" ? {
      closedAt: undefined,
      resolvedAt: undefined,
      resolvedByUserId: undefined,
      resolutionType: undefined,
      closureNote: undefined,
      reporterClosureMessage: undefined
    } : {}),
    updatedAt: input.now
  };
}

/**
 * Case evidence is intentionally unavailable while a report is only sitting in
 * intake. Claiming or explicitly marking the case as investigating moves it out
 * of these intake states; later waiting/escalation states retain investigation
 * access so staff do not lose their working record while awaiting a response.
 */
export function supportTicketInvestigationStarted(
  ticket: Pick<SupportTicket, "status">
): boolean {
  return ticket.status !== "new"
    && ticket.status !== "awaiting_triage"
    && ticket.status !== "open";
}

export function closeSupportTicket(ticket: SupportTicket, input: CloseSupportTicketInput): SupportTicket {
  assertTicketOpenForStaffAction(ticket);
  const closureNote = input.closureNote.trim();
  const reporterClosureMessage = input.reporterClosureMessage?.trim();
  if (closureNote.length < 5) throw new Error("Closing a case requires a meaningful staff closure note.");
  if (reporterClosureMessage !== undefined && reporterClosureMessage.length < 3) {
    throw new Error("The reporter closure message is too short.");
  }
  return {
    ...ticket,
    status: "closed",
    resolutionType: input.resolutionType,
    closureNote,
    ...(reporterClosureMessage ? { reporterClosureMessage } : {}),
    resolvedByUserId: input.actorUserId,
    resolvedAt: input.now,
    closedAt: input.now,
    updatedAt: input.now
  };
}

export function createSupportTicketTranscript(
  ids: IdGenerator,
  input: CreateSupportTicketTranscriptInput
): SupportTicketTranscript {
  if (input.ticket.status !== "closed") {
    throw new Error("Only closed tickets can be transcribed.");
  }

  const messages = [...input.messages].sort((left, right) => left.createdAt.getTime() - right.createdAt.getTime());

  return {
    id: ids.next("tickettranscript") as SupportTicketTranscriptId,
    ticketId: input.ticket.id,
    createdByUserId: input.createdByUserId,
    format: "markdown",
    content: renderTranscriptMarkdown(input.ticket, messages, input.discordTranscript),
    messageCount: Math.max(messages.length, input.discordMessageCount ?? 0),
    internalMessageCount: messages.filter((message) => message.internal).length,
    createdAt: input.now
  };
}

export function ticketViewPermissionForType(type: SupportTicketType): TicketViewPermission {
  return type === "staff_report" ? "tickets.staff_report.view" : "tickets.view";
}

export function ticketManagePermissionForType(type: SupportTicketType): TicketManagePermission {
  return type === "staff_report" ? "tickets.staff_report.view" : "tickets.manage";
}

function assertTicketOpenForStaffAction(ticket: SupportTicket): void {
  if (ticket.status === "closed") {
    throw new Error("Closed tickets cannot be changed.");
  }
}

function renderTranscriptMarkdown(ticket: SupportTicket, messages: SupportTicketMessage[], discordTranscript?: string): string {
  const lines = [
    `# ${ticket.reference}: ${ticket.subject}`,
    "",
    `- Type: ${ticket.type}`,
    `- Status: ${ticket.status}`,
    `- Priority: ${ticket.priority}`,
    `- Visibility: ${ticket.visibility}`,
    `- Creator: ${ticket.creatorUserId}`,
    ...(ticket.targetUserId ? [`- Target User: ${ticket.targetUserId}`] : []),
    ...(ticket.targetSteam64Id ? [`- Target Steam64: ${ticket.targetSteam64Id}`] : []),
    ...(ticket.claimedByUserId ? [`- Claimed By: ${ticket.claimedByUserId}`] : []),
    `- Created: ${ticket.createdAt.toISOString()}`,
    `- Updated: ${ticket.updatedAt.toISOString()}`,
    ...(ticket.closedAt ? [`- Closed: ${ticket.closedAt.toISOString()}`] : []),
    ...(ticket.resolutionType ? [`- Resolution: ${ticket.resolutionType}`] : []),
    ...(ticket.closureNote ? [`- Staff Closure Note: ${ticket.closureNote}`] : []),
    "",
    "## Opening Message",
    "",
    ticket.body,
    "",
    "## Messages"
  ];

  if (!messages.length && !discordTranscript) {
    lines.push("", "No messages were recorded.");
    return lines.join("\n");
  }

  for (const message of messages) {
    lines.push(
      "",
      `### ${message.createdAt.toISOString()} - ${message.authorUserId}${message.internal ? " (internal)" : ""}`,
      "",
      message.body
    );
  }

  if (discordTranscript?.trim()) {
    lines.push("", "## Discord Channel History", "", discordTranscript.trim());
  }

  return lines.join("\n");
}

export function supportTicketReference(id: SupportTicketId): string {
  const suffix = id.replace(/^ticket_/, "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  return `TN-${suffix.padStart(4, "0")}`;
}

export function supportTicketSequenceReference(sequence: number | bigint): string {
  const value = typeof sequence === "bigint" ? sequence : BigInt(sequence);
  if (value < 1n) throw new Error("Support ticket sequence must be positive.");
  return `TN-${value.toString().padStart(4, "0")}`;
}
